import * as React from 'react';
import { createContext, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Language, Mandant, MandantSettings } from '../types';
import { defaultTheme } from '../../Settings/config';
import {
  getCurrentLanguage, getDefaultOriginMandantSettings,
  getMandantSettingsFromOrigin,
} from './state-provider-utils';
import { IntlProvider } from 'react-intl';
import { getMessages } from '../../Intl/data';
import { getLanguageFromLocale } from '../../Intl/utils';
import { Loading } from '../../pages/system/Loading';
import { HomepageData, PageData, StatisticsData } from '../../pages/homepage/types';
import {
  InvalidConfigError,
  MessageFormatError,
  MissingDataError,
  MissingTranslationError,
  UnsupportedFormatterError,
} from '@formatjs/intl/src/error';
import { FormatError } from 'intl-messageformat';
import { getMandantSettingsByMandant } from '../mandant-settings-utils';
import { testDomains } from '../../Settings/test-settings';

export interface StateContextType {
  theme: Mandant;
  setTheme: (theme: Mandant) => void;
  originMandantSettings: MandantSettings;
  setOriginMandantSettings: (theme: MandantSettings) => void;
  language: Language;
  setLanguage: (language: Language) => void;
  pageData: PageData;
  homepageData: HomepageData[];
  statisticsData: StatisticsData[];
}

export const StateContext: React.Context<StateContextType> = createContext<StateContextType>(null);

export const StateProvider = ({children}: PropsWithChildren): ReactNode => {
  const [theme, _setTheme] = useState<Mandant>(null);
  const [originMandantSettings, _setOriginMandantSettings] = useState<MandantSettings>(null);
  const [language, setLanguage] = useState<Language>(getCurrentLanguage());
  const [pageData, setPageData] = useState<PageData>(undefined);
  const [homepageData, setHomepageData] = useState<HomepageData[]>(undefined);
  const [statisticsData, setStatisticsData] = useState<StatisticsData[]>(undefined);
  const [styled, setStyled] = useState<boolean>(false);

  useEffect((): void => {
    setStyle(theme);
    // wait a short moment before rendering to have the newly added css files processed
    setTimeout((): void => {
      setStyled(true);
    }, 50);
  }, [theme]);

  useEffect((): void => {
    const mandantSettings = getMandantSettingsFromOrigin();
    const defaultMandantSettings = getDefaultOriginMandantSettings();
    if(process.env.NODE_ENV !== 'production' || testDomains.some((testDomain: string): boolean => document.location.hostname.includes(testDomain))) {
      _setTheme(localStorage.getItem('theme') || mandantSettings?.mandant || defaultMandantSettings.mandant || defaultTheme);
      _setOriginMandantSettings(getMandantSettingsByMandant(localStorage.getItem('origin')) || mandantSettings || defaultMandantSettings);
    } else {
      setTheme(mandantSettings?.mandant || defaultMandantSettings.mandant || defaultTheme);
      setOriginMandantSettings(mandantSettings || defaultMandantSettings);
    }
  }, []);

  const setTheme = (themeMandant: Mandant): void => {
    localStorage.setItem('theme', themeMandant);
    _setTheme(themeMandant);
  };

  const setOriginMandantSettings = (origin: MandantSettings): void => {
    localStorage.setItem('origin', origin.mandant);
    _setOriginMandantSettings(origin);
  };

  const loadData = async (mandant: Mandant): Promise<void> => {
    setPageData(await require(`./../../Settings/mandants/${mandant}/data-page`));
    setHomepageData(await require(`./../../Settings/mandants/${mandant}/data-homepage`));
    setStatisticsData(await require(`./../../Settings/mandants/${mandant}/data-statistics`));
  };


  const setStyle = (mandant: Mandant): void => {
    if(!mandant) {
      return;
    }
    const colorLinkElement: HTMLElement = document.getElementById('app-color-mandant') as HTMLAnchorElement;
    if (colorLinkElement) {
      colorLinkElement.setAttribute('href', `${process.env.PUBLIC_URL}/themes/${mandant}.css`);
    }
    const styleLinkElement: HTMLElement = document.getElementById('app-style-mandant') as HTMLAnchorElement;
    if (styleLinkElement) {
      styleLinkElement.setAttribute('href', `${process.env.PUBLIC_URL}/style/${mandant}.css`);
    }
  };

  useEffect(() => {
    if(originMandantSettings?.mandant){
      void loadData(originMandantSettings.mandant);
    }
    if(originMandantSettings?.favicon) {
      let link: HTMLLinkElement = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = originMandantSettings.favicon;
    }


    (originMandantSettings?.jsScripts || []).forEach((script: string): void => {
      const scriptElement: HTMLScriptElement = document.createElement(`script`);
      scriptElement.text = script;
      document.head.appendChild(scriptElement);
    });
  }, [originMandantSettings]);



  const context: StateContextType = {
    theme,
    setTheme,
    originMandantSettings,
    setOriginMandantSettings,
    language,
    setLanguage,
    pageData,
    homepageData,
    statisticsData,
  };

  const messages: Record<string, string> = getMessages(getLanguageFromLocale(language));

  if(!styled) {
    return <></>;
  }
  return <StateContext.Provider value={context}>
    <IntlProvider
      locale={language}
      defaultLocale="de-DE"
      messages={messages}
      onError={(e: MissingTranslationError | MessageFormatError | MissingDataError | InvalidConfigError | UnsupportedFormatterError | FormatError): void => {
        if ((e as MissingTranslationError)?.descriptor?.id?.endsWith('.help')) {
          return;
        }
        console.error(e);
      }}
    >
      {homepageData && statisticsData && pageData ? children : <Loading/>}</IntlProvider>
  </StateContext.Provider>;
};
